import React from 'react';
import { Check, Clock, CircleDot, MinusCircle, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

const rejectedMeta = {
    color: 'bg-red-500',
    icon: <X className="w-3.5 h-3.5 text-white" />,
    label: 'Rejected',
    labelClass: 'text-red-600',
};

const notReachedMeta = {
    color: 'bg-slate-300',
    icon: <MinusCircle className="w-3.5 h-3.5 text-white" />,
    label: 'Not reached',
    labelClass: 'text-slate-400',
};

const stepStatusMeta = (status) => {
    switch (status) {
        case 'COMPLETED':
            return { color: 'bg-yavrio-green', icon: <Check className="w-3.5 h-3.5 text-white" />, label: 'Completed', labelClass: 'text-slate-500' };
        case 'ACTIVE':
            return { color: 'bg-[#2E2E2E]', icon: <CircleDot className="w-3.5 h-3.5 text-white" />, label: 'Awaiting approval', labelClass: 'text-slate-500' };
        case 'REJECTED':
            return rejectedMeta;
        case 'SKIPPED':
            return { color: 'bg-slate-400', icon: <MinusCircle className="w-3.5 h-3.5 text-white" />, label: 'Skipped', labelClass: 'text-slate-400' };
        default:
            return { color: 'bg-slate-300', icon: <Clock className="w-3.5 h-3.5 text-white" />, label: 'Pending', labelClass: 'text-slate-400' };
    }
};

const getRejectedStepOrder = (steps, paymentStatus) => {
    if (paymentStatus !== 'Rejected') return null;

    const explicit = steps.find((step) => step.status === 'REJECTED');
    if (explicit) return explicit.stepOrder;

    const active = steps.find((step) => step.status === 'ACTIVE');
    if (active) return active.stepOrder;

    const firstUnresolved = steps.find((step) => step.status !== 'COMPLETED' && step.status !== 'SKIPPED');
    return firstUnresolved?.stepOrder ?? null;
};

const resolveStepMeta = (step, paymentStatus, rejectedStepOrder) => {
    if (step.status === 'REJECTED' || (paymentStatus === 'Rejected' && step.stepOrder === rejectedStepOrder)) {
        return rejectedMeta;
    }

    if (paymentStatus === 'Rejected' && rejectedStepOrder !== null && step.stepOrder > rejectedStepOrder) {
        return notReachedMeta;
    }

    return stepStatusMeta(step.status);
};

const ApproverNames = ({ names }) => {
    if (!names.length) return null;

    const display = names.length > 3
        ? `${names.slice(0, 3).join(', ')}, ...`
        : names.join(', ');

    const label = (
        <p className="mt-1.5 mb-0 text-sm text-slate-400">
            {display}
        </p>
    );

    if (names.length <= 3) return label;

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <p className="mt-1.5 mb-0 text-sm text-slate-400 cursor-default">
                    {display}
                </p>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
                {names.join(', ')}
            </TooltipContent>
        </Tooltip>
    );
};

export default function ApprovalTimeline({ approval, paymentStatus }) {
    if (!approval || approval.type !== 'CUSTOM') {
        return null;
    }

    const steps = [...(approval.steps || [])].sort((a, b) => a.stepOrder - b.stepOrder);
    const rejectedStepOrder = getRejectedStepOrder(steps, paymentStatus);
    const isPaymentRejected = paymentStatus === 'Rejected';

    return (
        <TooltipProvider>
        <div className="w-full">
            {(approval.workflowId || approval.workflowVersion !== undefined) && (
                <p className="text-xs text-slate-400 mb-5 m-0">
                    {approval.workflowId && <span>Workflow {approval.workflowId}</span>}
                    {approval.workflowVersion !== undefined && (
                        <span>{approval.workflowId ? ' · ' : ''}Version {approval.workflowVersion}</span>
                    )}
                </p>
            )}

            <ol className={`relative border-l ml-2.5 ${isPaymentRejected ? 'border-red-200' : 'border-slate-200'}`}>
                {steps.map((step, index) => {
                    const meta = resolveStepMeta(step, paymentStatus, rejectedStepOrder);
                    const isRejectedStep = meta.label === 'Rejected';
                    const approverNames = step.approvers?.map((approver) => approver.name).filter(Boolean) || [];
                    const isLast = index === steps.length - 1;

                    return (
                        <li key={step.stepOrder} className={`ml-5 ${isLast ? 'pb-0' : 'pb-5'}`}>
                            <span
                                className={`absolute flex items-center justify-center w-5 h-5 rounded-full -left-2.5 ring-2 ring-white ${meta.color}`}
                            >
                                {meta.icon}
                            </span>
                            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                                <h4 className={`my-0 text-sm font-semibold ${isRejectedStep ? 'text-red-700' : 'text-[#2E2E2E]'}`}>
                                    Step {step.stepOrder + 1}
                                </h4>
                                <span className="text-xs text-slate-400">·</span>
                                <span className={`text-xs ${meta.labelClass || 'text-slate-500'}`}>{meta.label}</span>
                                {!isRejectedStep && meta.label !== 'Not reached' && (
                                    <span className="text-xs text-slate-400">
                                        · {step.obtainedApprovals}/{step.requiredApprovals} approved
                                    </span>
                                )}
                            </div>
                            <ApproverNames names={approverNames} />
                        </li>
                    );
                })}
            </ol>
        </div>
        </TooltipProvider>
    );
}
